'use client';

import { IMPERSONATE_ID_PERSISTED_STATE } from 'constants/persistedStates';
import {
  StyleGuide,
  StyleGuideCreateInput,
  StyleGuideSectionLanguageInput,
  User
} from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import usePersistentContext from 'hooks/usePersistenceState';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { styleGuideQueryFnAndKey } from 'providers/StyleGuideProviders/useStyleGuide';
import { useStyleGuideApprove } from 'providers/StyleGuideProviders/useStyleGuideApprove';
import { useStyleGuideCreate } from 'providers/StyleGuideProviders/useStyleGuideCreate';
import { useStyleGuideDestroy } from 'providers/StyleGuideProviders/useStyleGuideDestroy';
import { useStyleGuides } from 'providers/StyleGuideProviders/useStyleGuides';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { notifyException } from 'services/appsignal';
import queryClient from 'services/queryClient';

type TStyleGuidesContext = {
  isChecking: boolean;
  modalCreatedStyleGuideOpen: boolean;
  modalDeleteStyleGuideOpen: boolean;
  hasStyleGuides: boolean;
  styleGuides: Array<StyleGuide>;
  impersonateToken: unknown;
  currentUser?: User;
  handleOpenDeleteStyleGuideModal: (styleGuideId: string) => () => void;
  handleApproveStyleGuide: (styleGuideId: string) => () => Promise<void>;
  handleOpenDuplicateStyleGuideModal: (styleGuide: StyleGuide) => () => void;
  handleCloseDeleteStyleGuideModal: () => void;
  handleDeleteStyleGuide: () => void;
  handleEditStyleGuide: (id: string) => () => void;
  handleOnCancel: () => void;
  handleCreateStyleGuide: (data: StyleGuideCreateInput) => void;
};

const initialState: TStyleGuidesContext = {
  isChecking: true,
  hasStyleGuides: true,
  modalCreatedStyleGuideOpen: false,
  modalDeleteStyleGuideOpen: false,
  styleGuides: [],
  impersonateToken: undefined,
  currentUser: undefined,
  handleOpenDeleteStyleGuideModal: () => () => {},
  handleApproveStyleGuide: () => async () => {},
  handleOpenDuplicateStyleGuideModal: () => () => {},
  handleCloseDeleteStyleGuideModal: () => {},
  handleDeleteStyleGuide: () => {},
  handleEditStyleGuide: () => () => {},
  handleOnCancel: () => {},
  handleCreateStyleGuide: () => {}
};

const StyleGuidesContext = createContext<TStyleGuidesContext>(initialState);

const useStyleGuideContext = (): TStyleGuidesContext => useContext(StyleGuidesContext);

const StyleGuidesProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const t = useTranslations();
  const router = useRouter();
  const { data: currentUser } = useCurrentUser();
  const [impersonateToken] = usePersistentContext(IMPERSONATE_ID_PERSISTED_STATE);

  const { data, isLoading, isPending, refetch } = useStyleGuides();

  const { mutateAsync: styleGuideDestroy } = useStyleGuideDestroy();

  const { mutateAsync: styleGuideCreate } = useStyleGuideCreate();

  const { mutateAsync: styleGuideApprove } = useStyleGuideApprove();

  const isChecking = isLoading || isPending;
  const hasStyleGuides = !!data?.length && data?.length > 1;

  const [modalDeleteStyleGuideOpen, setModalDeleteStyleGuideOpen] = useState(false);
  const [modalCreatedStyleGuideOpen, setModalCreatedStyleGuideOpen] = useState(false);
  const [deleteStyleGuideId, setDeleteStyleGuideId] = useState('');

  const handleOpenDeleteStyleGuideModal = (styleGuideId: string) => () => {
    setModalDeleteStyleGuideOpen(true);
    setDeleteStyleGuideId(styleGuideId);
  };

  const handleCreateStyleGuide = async (data: StyleGuideCreateInput) => {
    try {
      await styleGuideCreate(data);
      refetch();
      setModalCreatedStyleGuideOpen(true);
    } catch (error) {
      console.error(error);
      toaster.error(
        t('messages.error.style_created.paragraph'),
        t('messages.error.style_created.title')
      );
    }
  };

  const handleOpenDuplicateStyleGuideModal = (styleGuide: StyleGuide) => async () => {
    try {
      const data = await queryClient.fetchQuery(styleGuideQueryFnAndKey({ id: styleGuide.id }));
      await styleGuideCreate({
        name: `${styleGuide.name} (2)`,
        sections: data?.sections?.reduce(
          (acc, sectionLanguage) => ({
            ...acc,
            [sectionLanguage.languageCode]: sectionLanguage.section
          }),
          {} as StyleGuideSectionLanguageInput
        ) as StyleGuideSectionLanguageInput
      });
      refetch();
      toaster.success(
        <span
          dangerouslySetInnerHTML={{ __html: t.raw('messages.success.style_cloned.paragraph') }}
        />,
        t('messages.success.style_cloned.title')
      );
    } catch (error) {
      console.error(error);
      toaster.error(
        t('messages.error.style_cloned.paragraph'),
        t('messages.error.style_cloned.title')
      );
    }
  };

  const handleCloseDeleteStyleGuideModal = () => {
    setDeleteStyleGuideId('');
    setModalDeleteStyleGuideOpen(false);
  };

  const handleDeleteStyleGuide = useCallback(async () => {
    try {
      const response = await styleGuideDestroy({
        styleGuideId: deleteStyleGuideId
      });
      if ((response?.errors?.length ?? 0) > 0) throw new Error(response?.errors?.join(', '));

      refetch();
      setModalDeleteStyleGuideOpen(false);
    } catch (error) {
      toaster.error(
        t('messages.error.style_deleted.paragraph'),
        t('messages.error.style_deleted.title')
      );
      notifyException(error);
      setModalDeleteStyleGuideOpen(false);
    }
  }, [deleteStyleGuideId, styleGuideDestroy, refetch, t]);

  const handleApproveStyleGuide = useCallback(
    (styleGuideId: string) => async () => {
      try {
        const response = await styleGuideApprove({
          styleGuideId
        });
        if ((response?.errors?.length ?? 0) > 0) throw new Error(response?.errors?.join(', '));

        refetch();
        toaster.success(t('messages.success.style_approve'));
      } catch (error) {
        toaster.error(
          t('messages.error.style_approve.paragraph'),
          t('messages.error.style_approve.title')
        );
        notifyException(error);
      }
    },
    [styleGuideApprove, refetch, t]
  );
  const handleEditStyleGuide = (id: string) => () => router.push(`/style-guides/edit/${id}` as any);

  const handleOnCancel = () => {
    router.push('/style-guides/list');
    setModalDeleteStyleGuideOpen(false);
    setDeleteStyleGuideId('');
    setModalCreatedStyleGuideOpen(false);
  };

  return (
    <StyleGuidesContext.Provider
      value={{
        isChecking,
        hasStyleGuides,
        styleGuides: data ?? [],
        modalCreatedStyleGuideOpen,
        modalDeleteStyleGuideOpen,
        impersonateToken,
        currentUser,
        handleOpenDeleteStyleGuideModal,
        handleApproveStyleGuide,
        handleOpenDuplicateStyleGuideModal,
        handleCloseDeleteStyleGuideModal,
        handleCreateStyleGuide,
        handleDeleteStyleGuide,
        handleEditStyleGuide,
        handleOnCancel
      }}
    >
      {children}
    </StyleGuidesContext.Provider>
  );
};

export { StyleGuidesProvider, useStyleGuideContext };
